import type {
  AuditStanding,
  AuditTally,
  FindingOutcome,
  FindingTally,
  FindingTrendPoint,
  ResponseTally,
} from './api.ts'
import { RECESSIVE, SERIES } from '../components/charts/palette.ts'
import type { Segment } from '../components/charts/segment.ts'
import { localDay } from './conferenceTime.ts'

/**
 * How the findings report (#551) says its numbers.
 *
 * Kept out of the page, like the conferência time helpers: which colour a
 * series wears, what a share is a share of, and how a week is named are
 * decisions somebody will quote, so they sit where a test can reach them.
 */

/** Where one finding stands. Mirrors `FindingState` in `@rp/core` by hand, like the rest. */
export enum FindingState {
  Corrected = 'corrected',
  NotAnIssue = 'not_an_issue',
  AnsweredOpen = 'answered_open',
  Unanswered = 'unanswered',
}

/**
 * Where a finding stands, in the order the funnel reads: worked first, then
 * what is still open. One colour each, the same on every chart of the screen.
 */
const FINDING_SERIES: ReadonlyArray<{
  readonly state: FindingState
  readonly key: keyof Pick<FindingTally, 'corrected' | 'notAnIssue' | 'answeredOpen' | 'unanswered'>
  readonly label: string
  readonly color: string
}> = [
  {
    state: FindingState.Corrected,
    key: 'corrected',
    label: 'Marcado para correção',
    color: SERIES.blue,
  },
  {
    state: FindingState.NotAnIssue,
    key: 'notAnIssue',
    label: 'Não era problema',
    color: SERIES.orange,
  },
  {
    state: FindingState.AnsweredOpen,
    key: 'answeredOpen',
    label: 'Respondido, sem concluir',
    color: RECESSIVE.strong,
  },
  {
    state: FindingState.Unanswered,
    key: 'unanswered',
    label: 'Sem resposta',
    color: RECESSIVE.weak,
  },
]

/** The states as the filter offers them: the legend's words and the legend's colours. */
export const FINDING_STATE_OPTIONS: ReadonlyArray<{
  readonly value: FindingState
  readonly label: string
  readonly tone: string
}> = FINDING_SERIES.map((series) => ({
  value: series.state,
  label: series.label,
  tone: series.color,
}))

export const FINDING_STATE_LABEL: Readonly<Record<FindingState, string>> = Object.fromEntries(
  FINDING_SERIES.map((series) => [series.state, series.label]),
) as Record<FindingState, string>

export function findingSegments(tally: FindingTally): Segment[] {
  return FINDING_SERIES.map(({ key, label, color }) => ({ key, label, color, value: tally[key] }))
}

/** What we said about the conferentes' answers, in the acompanhamento's words. */
const AUDIT_SERIES: ReadonlyArray<{
  readonly key: AuditStanding
  readonly label: string
  readonly color: string
}> = [
  { key: 'agreed', label: 'Concordamos', color: SERIES.aqua },
  { key: 'disagreed', label: 'Discordamos', color: SERIES.yellow },
  { key: 'pending', label: 'A classificar', color: RECESSIVE.strong },
  { key: 'unclassifiable', label: 'Fora do acompanhamento', color: RECESSIVE.weak },
]

export function auditSegments(tally: AuditTally): Segment[] {
  return AUDIT_SERIES.map((series) => ({ ...series, value: tally[series.key] }))
}

/** A trend column: the worked findings, and the open ones as one part. */
export function trendSegments(point: FindingTrendPoint): Segment[] {
  return [
    {
      key: 'corrected',
      label: 'Marcado para correção',
      value: point.corrected,
      color: SERIES.blue,
    },
    { key: 'notAnIssue', label: 'Não era problema', value: point.notAnIssue, color: SERIES.orange },
    { key: 'open', label: 'Em aberto', value: point.open, color: RECESSIVE.weak },
  ]
}

/**
 * Of the findings somebody worked, how many were real.
 *
 * Out of the worked ones only: a finding nobody answered has not been shown
 * right or wrong, and counting it either way would be inventing its answer.
 * Undefined, not zero, when nothing was worked -- there is no rate to show.
 */
export function precision(outcome: FindingOutcome): number | undefined {
  const judged = outcome.confirmed + outcome.falsePositives
  return judged === 0 ? undefined : outcome.confirmed / judged
}

export function formatRate(rate: number | undefined): string {
  return rate === undefined ? '—' : `${Math.round(rate * 100)}%`
}

export function formatCount(value: number): string {
  return value.toLocaleString('pt-BR')
}

/** An average with one decimal, or a dash when there is nothing to average over. */
export function formatAverage(total: number, over: number): string {
  if (over <= 0) return '—'
  return (total / over).toLocaleString('pt-BR', { maximumFractionDigits: 1 })
}

/** Coverage counts any saved answer, whether or not the conference was concluded. */
export function responseCoverage(responses: ResponseTally): number | undefined {
  return responses.total === 0 ? undefined : responses.responded / responses.total
}

/** Pending and unanswered rows have no known outcome and stay outside this ratio. */
export function comparisonEffectiveness(responses: ResponseTally): number | undefined {
  const known = responses.confirmed + responses.errors
  return known === 0 ? undefined : responses.confirmed / known
}

/**
 * How much of what could be classified has been, across the three tallies.
 *
 * What the audited numbers rest on: with three of forty classified, "after our
 * audit" is still thirty-seven conferentes' answers.
 */
export function auditCoverage(tallies: readonly AuditTally[]): {
  classified: number
  classifiable: number
} {
  let classified = 0
  let pending = 0
  for (const tally of tallies) {
    classified += tally.agreed + tally.disagreed
    pending += tally.pending
  }
  return { classified, classifiable: classified + pending }
}

/** `05/10`, or `sem. 05/10` for a week named by its Monday. */
export function formatBucket(from: string, grain: 'day' | 'week'): string {
  const [, month, day] = from.split('-')
  const short = `${day}/${month}`
  return grain === 'week' ? `sem. ${short}` : short
}

export const FINDINGS_MIN_DATE = '2026-10-01'

/** The findings dashboard starts at its supported cohort, never earlier. */
export function defaultFindingsWindow(now: Date): { from: string; to: string } {
  const today = localDay(now)
  return { from: FINDINGS_MIN_DATE, to: today < FINDINGS_MIN_DATE ? FINDINGS_MIN_DATE : today }
}
